import PropTypes from 'prop-types';
import React, { Component } from 'react';
import RosettaFormattedMessage from '../common/rosetta_formatted_message';
import ContentCaption from './content_caption';
import {
  findAspectRatioFromResource,
  REGEX_VIDEO_YOUTUBE
} from '../../utils/widgetInspector';
import {
  canEnableYoutubeApi,
  prepareEmbedCodeFromExternalVideo,
  generateEmbedCodeFromInternalVideo,
  initializeYoutubePlayer
} from '../../utils/video/youtube';
import { captureMessage } from '../../utils/browserSentry';
import withEscapeHatch from '../hocs/withEscapeHatch';
import { YT_READY_HANDLER_TIMEOUT } from '../../constants';
import {isFunction, nth, once, some} from 'lodash';
import { sendAnalyticsEvent } from '../../utils/analytics';

export class YoutubeSection extends Component {
  state = {
    memorySaveOverlayDismissed: false
  };

  componentDidMount() {
    if (this._container) {
      this.setupYoutubeApi(this._container.querySelector('iframe'));
    }
  }

  shouldComponentUpdate = (nextProps, nextState) =>
    some(
      ['resource', 'credit', 'description'],
      value => nextProps.content[value] !== this.props.content[value]
    ) || (
      nextState.memorySaveOverlayDismissed
      !== this.state.memorySaveOverlayDismissed
    );

  /*
   * There are two edge cases where we have to call `setupYoutubeApi` on update.
   *
   * - If there are two Youtube Sections next to each other in a slideshow.
   *   On slide change, the YoutubeSection stays mounted and does not trigger
   *   `componentDidMount`.
   * - If we are using the Memory Save Overlay, the video container is not
   *   present on mount.
   *
   * We handle both these cases using a `youtubeApiAttached` property on the
   * iframe itself.
   */
  componentDidUpdate() {
    if (this._container) {
      const iframe = this._container.querySelector('iframe');
      if (!iframe.youtubeApiAttached) {
        this.setupYoutubeApi(iframe);
      }
    }
  }

  setupYoutubeApi(iframe) {
    const { youtube_video_id, resource } = this.props.content;
    if (!youtube_video_id && !canEnableYoutubeApi(resource)) {
      captureMessage(
        'Rosetta tried to render a YoutubeSection, but it could not parse'
        + ' the embed code. The entire embed code is attached to this error'
        + ' event. Please take a look at it.',
        { extra: { embedCode: resource } }
      );
      return;
    }

    const playHandler = isFunction(this.props.playHandler)
      ? once(this.props.playHandler)
      : () => {};

    const youtubeCustomEvents = {
      onCompletion: this.props.completionHandler,
      onBuffer: this.props.clickHandler,
      onInitialCue: this.props.readyHandler,
      onPlay: playHandler
    };

    if (this.props.shouldAutoplay) {
      sendAnalyticsEvent('External Video', 'Play', 'Youtube');
    }

    initializeYoutubePlayer(iframe, youtubeCustomEvents);

    /*
     * The following line ensures that the Youtube API is set up if needed
     * after a `componentDidUpdate`. It applies to these two edge cases:
     * - Two YT slides next to each other
     * - For mobile slide sections, when a user clicks the Memory Save Overlay
     */
    iframe.youtubeApiAttached = true;
  }

  /*
   * In case 'onInitialCue' didn't fire the readyHandler, we fire the
   * `timeoutHandler` here after a timeout. Also log it to Sentry.
   */
  escapeHatch = () => {
    captureMessage(
      'The Youtube API did not call "onInitialCue" within 6 seconds.'
      + ' Check if there is anything wrong with the API integration.'
    );
    this.props.timeoutHandler();
  }

  extraYoutubeQuery = () => {
    const query = {};

    if (this.props.noRel) {
      query.rel = '0';
    }

    if (this.props.shouldHideInfo) {
      query.showinfo = '0';
    }

    if (this.props.shouldAutoplay) {
      query.autoplay = '1';
      query.mute = '1';
    }

    return query;
  };

  /*
   * Explanation of Memory Save Overlay
   * -------------------------------
   *
   * When a lot of videos are loaded on the page, and we are on a mobile browser,
   * the browser can crash because of all the iframes and scripts being loaded
   * in.
   *
   * This is most common on mobile slideshows, since we often have slideshows
   * with dozens of videos. Therefore, we show a Memory Save Overlay on top of
   * YT sections on mobile slideshows.
   *
   * - we don't load the iframe immediately, instead showing a static play button
   * - clicking the play button loads the iframe
   *
   * If we encounter these memory issues on types of pages other than mobile
   * slideshows, we can show the memory save overlay on those too.
   */
  shouldShowMemorySaveOverlay = () =>
    this.props.isMobileSlideSection && !this.state.memorySaveOverlayDismissed;

  memorySaveOverlay() {
    const { resource } = this.props.content;
    const youtubeId = nth(resource.match(REGEX_VIDEO_YOUTUBE), 1);
    if (!youtubeId) {
      // This should never happen, but just in case...
      return this.videoContainer();
    }

    return (
      <div
        className="memory-save-overlay"
        onClick={this.handleMemorySaveOverlayClicked}
      >
        <img src={`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`} />
        <button>
          <RosettaFormattedMessage parentFilename={__filename}
            id="mobile_load_cta"
            description="Button to load the video for mobile slide section YT videos"
            defaultMessage="Load Video"
          />
        </button>
      </div>
    );
  }

  handleMemorySaveOverlayClicked = () => {
    this.setState({ memorySaveOverlayDismissed: true });
  }

  videoContainer() {
    const { youtube_video_id, resource, meta_title } = this.props.content;
    const aspectRatio = findAspectRatioFromResource(resource);
    let innerHTML;
    if (youtube_video_id) {
      innerHTML = generateEmbedCodeFromInternalVideo(
        youtube_video_id, this.extraYoutubeQuery(), this.context.location, meta_title
      );
    } else {
      innerHTML = canEnableYoutubeApi(resource)
        ? prepareEmbedCodeFromExternalVideo(
          resource, this.extraYoutubeQuery(), this.context.location
        )
        : resource;
    }

    return (
      <div className={`video-container ratio-${aspectRatio}`}
        dangerouslySetInnerHTML={{ __html: innerHTML }}
        ref={node => this._container = node}
      />
    );
  }

  contentCaption() {
    if (this.props.shouldHideContentCaption) {
      return null;
    }
    const { credit, description } = this.props.content;
    return <ContentCaption credit={credit} description={description} />;
  }

  render = () => (
    <div className="section-external-video youtube">
      {
        this.shouldShowMemorySaveOverlay()
          ? this.memorySaveOverlay()
          : this.videoContainer()
      }
      {this.contentCaption()}
    </div>
  );
}

export default withEscapeHatch({
  timeout: YT_READY_HANDLER_TIMEOUT,
  logEscapeHatch: () => {
    captureMessage(
      'The Youtube API did not call "onInitialCue" within 6 seconds.'
      + ' Check if there is anything wrong with the API integration.'
    );
  }
})(YoutubeSection);

YoutubeSection.propTypes = {
  content: PropTypes.shape({
    resource: PropTypes.string.isRequired,
    credit: PropTypes.string,
    description: PropTypes.string,
    youtube_video_id: PropTypes.string,
    meta_title: PropTypes.string
  }),
  isFirstSection: PropTypes.bool.isRequired,
  isMobileSlideSection: PropTypes.bool,
  readyHandler: PropTypes.func,
  playHandler: PropTypes.func,
  clickHandler: PropTypes.func,
  timeoutHandler: PropTypes.func,
  completionHandler: PropTypes.func,
  shouldAutoplay: PropTypes.bool,
  shouldHideContentCaption: PropTypes.bool,
  shouldHideInfo: PropTypes.bool,
  noRel: PropTypes.bool
};

YoutubeSection.contextTypes = {
  location: PropTypes.object
};
